import React, { useEffect, useMemo, useState } from 'react'
import { portal } from '../portalApi.js'

// 계정과목(2차) 아래 세목(3차)을 관리한다.
//
// 손익계산서는 계속 2차로 집계하고, 실무자는 3차를 고른다. 위하고와 숫자가 어긋나지 않으면서
// 안쪽은 잘게 볼 수 있다. 변동·고정은 BEP 가 쓰는 값이라 세목마다 붙인다 - 지급수수료 한
// 계정 안에도 결제대행수수료(변동)와 신탁보수(고정)가 섞여 있다.
//
// 세목을 둘 이유가 없는 계정(급여·상각비)은 계정 자체에 변동·고정을 단다. 그런 계정이
// 판관비에서 덩치가 가장 커서 빼놓으면 BEP 가 안 선다.

const 변동 = '변동'
const 고정 = '고정'

// 대분류 순서는 손익계산서를 읽는 순서와 같게 둔다
const 순서 = ['매출', '매출원가', '제조원가', '판매관리비', '영업외수익', '영업외비용', '법인세등']

function CostBadge({ value }) {
  if (!value) return <span className="muted" style={{ fontSize: 11 }}>—</span>
  const 변 = value === 변동
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 10,
      background: 변 ? '#FEF3C7' : '#E0F2FE', color: 변 ? '#92400E' : '#075985',
    }}>{value}</span>
  )
}

function SubitemChip({ item, onEdit, onDelete }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px',
      border: '1px solid var(--line, #E2E8F0)', borderRadius: 6, marginRight: 6, marginBottom: 6,
      background: item.used ? 'transparent' : 'rgba(148,163,184,.14)',
    }}>
      <span style={{ fontSize: 13, textDecoration: item.used ? 'none' : 'line-through' }}>
        {item.name}
      </span>
      <CostBadge value={item.costType} />
      <button className="btn secondary" style={{ padding: '1px 6px', fontSize: 10 }}
        onClick={() => onEdit(item)}>수정</button>
      <button className="btn danger" style={{ padding: '1px 6px', fontSize: 10 }}
        onClick={() => onDelete(item)}>삭제</button>
    </span>
  )
}

function SubitemForm({ code, accountName, initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || '')
  const [costType, setCostType] = useState(initial?.costType || '')
  const [used, setUsed] = useState(initial ? initial.used : true)

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
        placeholder={`${accountName} 아래 세목`} style={{ width: 150 }}
        onKeyDown={(e) => { if (e.key === 'Enter') onSave({ code, name, costType, used }) }} />
      <select value={costType} onChange={(e) => setCostType(e.target.value)} style={{ width: 92 }}>
        <option value="">변동/고정</option>
        <option value={변동}>{변동}</option>
        <option value={고정}>{고정}</option>
      </select>
      {initial && (
        <label className="muted" style={{ fontSize: 11, display: 'inline-flex', gap: 4 }}>
          <input type="checkbox" checked={used} onChange={(e) => setUsed(e.target.checked)} />쓰는 중
        </label>
      )}
      <button className="btn" style={{ padding: '3px 9px', fontSize: 11 }}
        disabled={!name.trim()} onClick={() => onSave({ code, name, costType, used })}>저장</button>
      <button className="btn secondary" style={{ padding: '3px 9px', fontSize: 11 }}
        onClick={onCancel}>취소</button>
    </span>
  )
}

export default function Subitems() {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [major, setMajor] = useState('판매관리비')
  const [q, setQ] = useState('')
  const [adding, setAdding] = useState(null)     // 세목을 새로 다는 계정코드
  const [editing, setEditing] = useState(null)   // {code, ...세목}
  const [toast, setToast] = useState('')
  const [세목만, set세목만] = useState(false)

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2200) }

  const load = async () => {
    setErr('')
    try { setData(await portal.get('/api/meta/subitems')) }
    catch (e) { setErr(e.message) }
  }
  useEffect(() => { load() }, [])

  const majors = useMemo(() => {
    if (!data) return []
    const 있는것 = [...new Set(data.accounts.map((a) => a.major).filter(Boolean))]
    return [...순서.filter((m) => 있는것.includes(m)),
            ...있는것.filter((m) => !순서.includes(m))]
  }, [data])

  const rows = useMemo(() => {
    if (!data) return []
    const 말 = q.trim()
    return data.accounts.filter((a) => {
      if (major !== '전체' && a.major !== major) return false
      if (세목만 && !a.subitems.length) return false
      if (!말) return true
      return a.name.includes(말) || a.code.includes(말) ||
             a.subitems.some((s) => s.name.includes(말))
    })
  }, [data, major, q, 세목만])

  const 합계 = useMemo(() => {
    if (!data) return { 계정: 0, 세목: 0, 변동: 0, 고정: 0 }
    let 세목 = 0, v = 0, f = 0
    for (const a of data.accounts) {
      for (const s of a.subitems) {
        세목 += 1
        if (s.costType === 변동) v += 1
        if (s.costType === 고정) f += 1
      }
    }
    return { 계정: data.accounts.length, 세목, 변동: v, 고정: f }
  }, [data])

  const save = async (body) => {
    try {
      await portal.post('/api/meta/subitems', body)
      setAdding(null); setEditing(null)
      notify('저장했습니다')
      load()
    } catch (e) { setErr(e.message) }
  }

  const saveAccountCost = async (code, costType) => {
    try {
      await portal.post('/api/meta/subitems', { code, accountCostType: costType })
      notify('저장했습니다')
      load()
    } catch (e) { setErr(e.message) }
  }

  const remove = async (item) => {
    if (!window.confirm(`"${item.name}" 세목을 지울까요?`)) return
    try {
      const r = await portal.del(`/api/meta/subitems/${item.id}`)
      notify(r?.message || '지웠습니다')
      load()
    } catch (e) { setErr(e.message) }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
        <button className={`btn ${major === '전체' ? '' : 'secondary'}`}
          onClick={() => setMajor('전체')}>전체</button>
        {majors.map((m) => (
          <button key={m} className={`btn ${major === m ? '' : 'secondary'}`}
            onClick={() => setMajor(m)}>{m}</button>
        ))}
        <input placeholder="계정과목·세목 검색" style={{ width: 200 }}
          value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="muted" style={{ fontSize: 12, display: 'inline-flex', gap: 5 }}>
          <input type="checkbox" checked={세목만} onChange={(e) => set세목만(e.target.checked)} />
          세목 있는 것만
        </label>
        <span className="muted" style={{ marginLeft: 'auto', fontSize: 12 }}>
          계정 {합계.계정} · 세목 {합계.세목} (변동 {합계.변동} / 고정 {합계.고정})
        </span>
      </div>

      <div className="hint" style={{ marginBottom: 10 }}>
        손익계산서는 <b>계정과목(2차)</b> 기준으로 나갑니다 — 세목을 아무리 나눠도 위하고와 맞는 숫자는 바뀌지 않습니다.
        위하고로 내보낼 때는 품명·적요 앞에 <code>[세목]</code>을 붙여 보냅니다.
        <br />
        <b>변동/고정</b>은 손익분기점(BEP) 계산이 쓰는 값입니다. 세목에 붙인 값이 계정 값보다 먼저입니다.
        세목이 없는 계정(급여·상각비처럼 나눌 필요가 없는 것)은 계정 자체에 지정하세요.
      </div>

      {err && <div className="error-box">{err}</div>}
      {!err && !data && <div className="muted">불러오는 중…</div>}

      {data && (
        <div className="panel">
          <table>
            <thead>
              <tr>
                <th className="l" style={{ width: 110 }}>대분류</th>
                <th className="l" style={{ width: 190 }}>계정과목</th>
                <th className="l">세목</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={3} className="c muted">해당하는 계정과목이 없습니다</td></tr>
              )}
              {rows.map((a) => (
                <tr key={a.code}>
                  <td className="l muted" style={{ fontSize: 12 }}>
                    {a.major}{a.minor ? <><br /><span style={{ fontSize: 11 }}>{a.minor}</span></> : null}
                  </td>
                  <td className="l">
                    <span style={{ fontWeight: 500 }}>{a.name}</span>{' '}
                    <span className="muted" style={{ fontSize: 11 }}>{a.code}</span>
                    {a.subitems.length === 0 && (
                      <div style={{ marginTop: 4 }}>
                        <select value={a.costType || ''} style={{ width: 104, fontSize: 12 }}
                          onChange={(e) => saveAccountCost(a.code, e.target.value)}>
                          <option value="">변동/고정</option>
                          <option value={변동}>{변동}</option>
                          <option value={고정}>{고정}</option>
                        </select>
                      </div>
                    )}
                  </td>
                  <td className="l">
                    {a.subitems.map((s) => (
                      editing && editing.id === s.id
                        ? <SubitemForm key={s.id} code={a.code} accountName={a.name} initial={s}
                            onSave={save} onCancel={() => setEditing(null)} />
                        : <SubitemChip key={s.id} item={s}
                            onEdit={(it) => { setAdding(null); setEditing({ ...it, code: a.code }) }}
                            onDelete={remove} />
                    ))}
                    {adding === a.code
                      ? <SubitemForm code={a.code} accountName={a.name}
                          onSave={save} onCancel={() => setAdding(null)} />
                      : <button className="btn secondary" style={{ padding: '3px 9px', fontSize: 11 }}
                          onClick={() => { setEditing(null); setAdding(a.code) }}>+ 세목 추가</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
